import type { FlagGrant } from '../schemas/flag';

/**
 * Who is holding a museum's flag up, said in words (ADR 0010 §2).
 *
 * The **Set by** column used to render off a binary — this museum's row, or the
 * platform default — and that binary stopped being true when the row started
 * reference counting. A flag can be on for a museum because somebody set it by
 * hand, because the platform default says so, *or* because the museum bought a
 * product or a component whose grant holds the row up. The third case rendered
 * as "This client", which named the wrong party: nobody here decided it, and an
 * operator who removes the override to undo it removes nothing.
 *
 * The withdrawal dialog needs the same facts one sentence further on, and it is
 * the case the column cannot show that matters most there — see
 * {@link withdrawGrantCopy}. Both read this module so the column and the dialog
 * cannot drift into naming the same grant two different ways.
 *
 * Pure, like `override-confirm-copy.ts` and `platform-default-confirm-copy.ts`,
 * and for the same reason: `apps/web` runs vitest on `node` with no jsdom. A
 * sentence a component assembles for itself is a sentence no test can read.
 *
 * ## Why the catalog key is rendered raw
 *
 * A grant carries `{kind, id}` and nothing else, and there is no catalog on
 * this side to resolve `museum-pro` into a name a human chose. Title-casing it
 * would invent one — and an invented name is worse than a key, because the
 * operator cannot search the catalog for it. So the kind is the label and the
 * key is quoted verbatim, until a real catalog lookup exists to replace it.
 */

const KIND_LABEL: Record<FlagGrant['kind'], string> = {
    /** Not "manual": the column's question is *who*, and the answer is this museum. */
    manual: 'This client',
    product: 'Product',
    component: 'Component',
};

/** The middle dot the whole feature separates provenance fragments with. */
const SEPARATOR = ' · ';

/** What this museum inherits when it has no row of its own. */
const INHERITED = 'Platform default';

/** One grant, named. `This client` for the hand-made one, `Product <key>` otherwise. */
export function grantName(grant: FlagGrant): string {
    return grant.kind === 'manual'
        ? KIND_LABEL.manual
        : `${KIND_LABEL[grant.kind]} ${String(grant.id)}`;
}

/**
 * The order grants are named in, and it is this module's rather than the
 * array's.
 *
 * What the API serialises is the order the grants were written in, which moves
 * every time a product is re-granted — a column that reshuffled itself between
 * two page loads would read as a change nobody made. So: the hand-made grant
 * first, because it is the only one an operator on this screen can act on, then
 * the catalog grants by kind and then by key, both ascending. Nothing about the
 * result depends on what arrived first.
 */
export function orderedGrants(grants: readonly FlagGrant[]): readonly FlagGrant[] {
    return [...grants].sort((a, b) => {
        if (a.kind === 'manual' || b.kind === 'manual') return a.kind === 'manual' ? -1 : 1;
        return a.kind === b.kind ? a.id.localeCompare(b.id) : a.kind.localeCompare(b.kind);
    });
}

/** Several grants, named in a stable order and joined with the middle dot. */
export function grantNames(grants: readonly FlagGrant[]): string {
    return orderedGrants(grants).map(grantName).join(SEPARATOR);
}

/**
 * What the column is rendering for — the two fields of `TenantFlagRow` it
 * needs, so the cell hands the row over whole and decides nothing itself.
 *
 * Whether the museum has a row of its own is `origin`, never `grants.length`:
 * an override written before provenance existed carries no grantors at all, and
 * reading that as "inherited" would hide a row that is in fact pinning this
 * museum against the next platform-wide default change.
 */
export interface RowProvenance {
    readonly origin: 'override' | 'default';
    readonly grants: readonly FlagGrant[];
}

/**
 * One fragment of the **Set by** cell. Split rather than handed over as one
 * string because the cell styles them differently — this museum's own decision
 * is not the same kind of fact as a grant that came with a purchase — and the
 * component must not have to re-derive which is which from the text.
 */
export interface SetBySegment {
    readonly kind: 'manual' | 'catalog' | 'default';
    readonly label: string;
}

export function setBySegments(provenance: RowProvenance): readonly SetBySegment[] {
    if (provenance.origin === 'default') return [{ kind: 'default', label: INHERITED }];

    // A row that predates provenance has no grantors to list, and the honest
    // answer for it is the one this column always gave: somebody set it here.
    if (provenance.grants.length === 0) return [{ kind: 'manual', label: KIND_LABEL.manual }];

    return orderedGrants(provenance.grants).map((grant) => ({
        kind: grant.kind === 'manual' ? ('manual' as const) : ('catalog' as const),
        label: grantName(grant),
    }));
}

/** The same cell as one string — what a test can read, and a tooltip can use. */
export function setByText(provenance: RowProvenance): string {
    return setBySegments(provenance)
        .map((segment) => segment.label)
        .join(SEPARATOR);
}

/**
 * What the row will do once this museum's hand-made grant is withdrawn.
 *
 * Two different outcomes, and the dialog used to state only the first:
 *
 * - The row is **on**. The catalog grant was already granting it, so removing
 *   the hand-made one moves nothing. "The flag will stay on" is true.
 *
 *   It says *setting*, not *override*, and that is not a softening: the
 *   override row is **not** removed here. The catalog grant keeps holding it up
 *   and only the hand-made grant is withdrawn, so a sentence promising to
 *   remove the override would describe something the platform does not do.
 *   Reference counting is ours to know; what the operator needs is what they
 *   took away and what the museum ends up seeing.
 * - The row is **off** — its override says `enabled: false` while a catalog
 *   grant sits underneath it. That override is the only thing holding the flag
 *   down. Remove it and the row falls back to a grant that *grants* the flag,
 *   and the value flips on. A dialog that said "the flag will stay on" there
 *   would be telling an operator nothing changes while they turn a feature on
 *   for a customer.
 *
 * Both name the remaining grants through {@link grantNames}, so the sentence
 * and the **Set by** column agree on what the thing is called.
 */
export function withdrawGrantCopy(row: {
    readonly enabled: boolean;
    readonly remaining: readonly FlagGrant[];
}): { readonly title: string; readonly description: string } {
    const names = grantNames(row.remaining);
    const plural = row.remaining.length > 1;

    if (row.enabled) {
        return {
            title: 'The flag will stay on',
            description:
                `${names} also ${plural ? 'grant' : 'grants'} this flag. Removing this ` +
                `client's own setting stops it being pinned here by hand, but the flag ` +
                `stays on for them.`,
        };
    }

    return {
        title: `The flag will follow ${names}`,
        description:
            `This client's own override is what keeps this flag off. Removing it hands the ` +
            `flag back to ${names}, which ${plural ? 'grant' : 'grants'} it — so the flag ` +
            `will turn on for this client.`,
    };
}
